import { Listbox, Transition } from '@headlessui/react'
import type { ReactNode, FocusEvent, PropsWithChildren } from 'react'
import { Fragment } from 'react'
import { ArrowDownIcon } from '~/components/icons/ArrowDownIcon'
import type { FieldProps } from '~/components/shared/TextField'
import { classNames } from '~/helpers/string'

export type SelectProps<T> = PropsWithChildren<
  {
    id?: string
    name?: string | undefined
    value?: T
    onChange?: (value: T) => void
    renderSelected?: (selected: T) => ReactNode
    defaultValue?: T
    onBlur?: (event: FocusEvent<HTMLUListElement, Element>) => void
  } & FieldProps
>

function Select<T>(props: SelectProps<T>) {
  const {
    value,
    onChange,
    label,
    renderSelected,
    id,
    error,
    helperText = '',
    optional,
    name,
    disabled,
    children,
    defaultValue,
    onBlur,
  } = props
  return (
    <Listbox name={name} value={value} onChange={onChange} disabled={disabled} defaultValue={defaultValue}>
      {({ open, value }) => {
        return (
          <div className="relative min-w-32">
            <label
              className={classNames(
                `z-2 absolute left-3 top-2 -translate-y-4 transform bg-white px-1 text-xs duration-100`,
                error
                  ? 'text-brand-red-700 focus:outline-brand-red-700'
                  : 'text-brand-gray-500 peer-focus:text-brand-purple-700',
                disabled ? 'text-opacity-35' : '',
              )}
              htmlFor={id}
            >
              {label} {!optional && <span className="asterisk"></span>}
            </label>
            <Listbox.Button
              className={classNames(
                'block h-[46px] w-full rounded-md bg-transparent px-[15px] py-[13px] text-sm font-normal focus:outline-none disabled:pointer-events-none',
                'border border-brand-neutral-200  focus:border-brand-purple-700',
                'focus-visible:border-transparent focus-visible:shadow-[0_0_0_2px_#671ddb]',
                open ? 'border-transparent shadow-[0_0_0_2px_#671ddb] duration-150' : 'hover:border-brand-neutral-500',
                error ? 'border-2 border-brand-red-700 focus:border-2 focus:border-brand-red-700 ' : '',
              )}
            >
              <div className={classNames('flex items-center justify-between', disabled ? 'text-gray-300' : '')}>
                {typeof renderSelected === 'function' ? renderSelected(value) : <span>{JSON.stringify(value)}</span>}
                <span className="ml-auto">
                  <ArrowDownIcon
                    className={`${open ? 'rotate-180' : ''} h-6 w-6 dark:text-success-600`}
                    aria-hidden="true"
                  />
                </span>
              </div>
            </Listbox.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Listbox.Options
                onBlur={(e) => {
                  setTimeout(() => {
                    onBlur?.(e)
                  }, 100)
                }}
                className="absolute z-[20] mt-1 max-h-48 w-full overflow-y-auto rounded-lg bg-base-100 py-1 text-base-content shadow-lg focus:outline-none"
              >
                {children}
              </Listbox.Options>
            </Transition>
            {helperText && (
              <span
                className={classNames(
                  'ml-4 mt-1 text-xs font-normal leading-5',
                  error ? ' text-brand-red-700' : 'mt-[5px] text-brand-actions-progress',
                  disabled ? 'text-brand-violet-950 text-opacity-35' : '',
                )}
              >
                {helperText}
              </span>
            )}
          </div>
        )
      }}
    </Listbox>
  )
}

type SelectItemProps<T> = PropsWithChildren<{
  value: T
}>
function SelectItem<T>(props: SelectItemProps<T>) {
  const { value, children } = props
  return (
    <Listbox.Option
      value={value}
      className={({ active }) =>
        `relative cursor-default select-none py-2 pl-3 pr-4 ${
          active ? 'bg-primary-100 text-base-content' : 'text-base-content'
        }`
      }
    >
      <div className="text-sm">{children}</div>
    </Listbox.Option>
  )
}

Select.Item = SelectItem
export default Select
